import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';

const EditMapModule = () => {
  const { currentUser } = useAuth();
  
  // Form state
  const [formData, setFormData] = useState({
    selectedModule: '',
    selectedModuleId: '',
    selectedDegreePrograms: [],
    year: '',
    isMandatory: true
  });

  // Data lists
  const [modulesList, setModulesList] = useState([]);
  const [degreeProgramsList, setDegreeProgramsList] = useState([]);
  const [existingMappings, setExistingMappings] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load data function - defined outside useEffect so it can be reused
  const loadData = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      
      // Get admin's university_id
      let universityId = currentUser?.university_id;
      
      if (!universityId && currentUser?.id) {
        // If university_id not in user context, get from admins table
        const { data: adminData, error: adminError } = await supabase
          .from('admins')
          .select('university_id')
          .eq('user_id', currentUser.id)
          .single();
        
        if (!adminError && adminData) {
          universityId = adminData.university_id;
        }
      }

      if (!universityId) {
        console.error('Unable to determine university_id for data filtering');
        alert('Unable to determine your university. Please contact support.');
        return;
      }

      console.log('Loading data for university:', universityId);

      // Load modules from Supabase
      const { data: modulesData, error: modulesError } = await supabase
        .from('modules')
        .select('id, module_name, module_code, active')
        .eq('university_id', universityId)
        .eq('active', true)
        .order('module_name', { ascending: true });

      if (modulesError) {
        console.error('Error loading modules:', modulesError);
        throw modulesError;
      }

      console.log(`Loaded ${modulesData?.length || 0} modules for university ${universityId}`);
      setModulesList(modulesData || []);

      // Load existing module-degree mappings
      const { data: mappingsData, error: mappingsError } = await supabase
        .from('module_degree_mappings')
        .select(`
          id,
          module_id,
          degree_details_id,
          year,
          is_mandatory,
          modules!inner (
            id,
            module_name,
            module_code,
            university_id
          ),
          degree_details!inner (
            id,
            degree_programme,
            local_international,
            degree_level,
            university_id
          )
        `)
        .eq('modules.university_id', universityId)
        .eq('degree_details.university_id', universityId);

      if (mappingsError) {
        console.error('Error loading existing mappings:', mappingsError);
        throw mappingsError;
      }

      console.log(`Loaded ${mappingsData?.length || 0} existing mappings for university ${universityId}`);
      setExistingMappings(mappingsData || []);

      // Load degree programs from Supabase
      const { data: degreeData, error: degreeError } = await supabase
        .from('degree_details')
        .select('id, degree_programme, local_international, degree_level, active')
        .eq('university_id', universityId)
        .eq('active', true)
        .order('degree_programme', { ascending: true });

      if (degreeError) {
        console.error('Error loading degree programs:', degreeError);
        throw degreeError;
      }

      // Format degree programs for display
      const formattedDegreePrograms = degreeData?.map(degree => ({
        id: degree.id,
        displayName: `${degree.degree_programme} (${degree.local_international}) (${degree.degree_level})`,
        originalData: degree
      })) || [];

      console.log(`Loaded ${formattedDegreePrograms.length} degree programs for university ${universityId}`);
      setDegreeProgramsList(formattedDegreePrograms);
      
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    if (currentUser) {
      loadData();
    }
  }, [currentUser]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle module selection
  const handleModuleChange = (e) => {
    const selectedModuleId = e.target.value;
    const selectedModule = modulesList.find(m => m.id === selectedModuleId);
    
    if (selectedModule) {
      // Find existing mappings for this module
      const moduleMappings = existingMappings.filter(mapping => mapping.module_id === selectedModuleId);
      
      console.log('Found existing mappings for module:', moduleMappings);
      
      // Extract years and degree IDs from existing mappings
      const mappedYears = [...new Set(moduleMappings.map(mapping => mapping.year))];
      const mappedDegreeIds = moduleMappings.map(mapping => mapping.degree_details_id);
      const isMandatory = moduleMappings.length > 0 ? moduleMappings[0].is_mandatory : true;
      
      setFormData(prev => ({
        ...prev,
        selectedModuleId: selectedModuleId,
        selectedModule: selectedModule.module_name,
        year: mappedYears.length > 0 ? mappedYears[0].toString() : '', // Use first year found
        selectedDegreePrograms: mappedDegreeIds,
        isMandatory: isMandatory
      }));
      
      console.log('Updated form with existing mappings:', {
        selectedModule: selectedModule.module_name,
        selectedModuleId: selectedModuleId,
        year: mappedYears.length > 0 ? mappedYears[0] : '',
        selectedDegreePrograms: mappedDegreeIds,
        isMandatory: isMandatory,
        totalMappings: moduleMappings.length
      });
    } else {
      setFormData(prev => ({
        ...prev,
        selectedModuleId: '',
        selectedModule: '',
        year: '',
        selectedDegreePrograms: [],
        isMandatory: true
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Current formData on submit:', formData);
    
    if (!formData.selectedModuleId || formData.selectedDegreePrograms.length === 0 || !formData.year) {
      alert('Please fill in all required fields');
      return;
    }

    if (!currentUser) {
      alert('Please log in to edit mappings');
      return;
    }

    try {
      setLoading(true);

      // Get existing mappings for this module
      const existingModuleMappings = existingMappings.filter(mapping => mapping.module_id === formData.selectedModuleId);
      
      // Get currently mapped degree IDs
      const existingDegreeIds = existingModuleMappings.map(mapping => mapping.degree_details_id);
      
      // Find degrees to remove (were mapped before but not selected now)
      const degreesToRemove = existingDegreeIds.filter(degreeId => !formData.selectedDegreePrograms.includes(degreeId));
      
      // Find degrees to add (selected now but not mapped before)
      const degreesToAdd = formData.selectedDegreePrograms.filter(degreeId => !existingDegreeIds.includes(degreeId));
      
      // Find degrees to update (already mapped and still selected - check for year/mandatory changes)
      const degreesToUpdate = formData.selectedDegreePrograms.filter(degreeId => existingDegreeIds.includes(degreeId));

      console.log('Edit operation analysis:', {
        existingDegreeIds,
        selectedDegreeIds: formData.selectedDegreePrograms,
        degreesToRemove,
        degreesToAdd,
        degreesToUpdate
      });

      // Remove mappings that are no longer selected
      if (degreesToRemove.length > 0) {
        const mappingsToRemove = existingModuleMappings.filter(mapping => degreesToRemove.includes(mapping.degree_details_id));
        const removeIds = mappingsToRemove.map(mapping => mapping.id);
        
        const { error: removeError } = await supabase
          .from('module_degree_mappings')
          .delete()
          .in('id', removeIds);

        if (removeError) {
          console.error('Error removing mappings:', removeError);
          throw removeError;
        }
        console.log(`Removed ${removeIds.length} mappings`);
      }

      // Add new mappings
      if (degreesToAdd.length > 0) {
        const newMappings = degreesToAdd.map(degreeId => ({
          module_id: formData.selectedModuleId,
          degree_details_id: degreeId,
          year: parseInt(formData.year),
          is_mandatory: formData.isMandatory,
          created_at: new Date().toISOString()
        }));

        const { error: addError } = await supabase
          .from('module_degree_mappings')
          .insert(newMappings);

        if (addError) {
          console.error('Error adding new mappings:', addError);
          throw addError;
        }
        console.log(`Added ${newMappings.length} new mappings`);
      }

      // Update existing mappings (year and mandatory status)
      if (degreesToUpdate.length > 0) {
        const updatePromises = degreesToUpdate.map(async (degreeId) => {
          const existingMapping = existingModuleMappings.find(mapping => mapping.degree_details_id === degreeId);
          
          // Only update if year or mandatory status has changed
          if (existingMapping && 
              (existingMapping.year !== parseInt(formData.year) || 
               existingMapping.is_mandatory !== formData.isMandatory)) {
            
            const { error: updateError } = await supabase
              .from('module_degree_mappings')
              .update({
                year: parseInt(formData.year),
                is_mandatory: formData.isMandatory
              })
              .eq('id', existingMapping.id);

            if (updateError) {
              console.error('Error updating mapping:', updateError);
              throw updateError;
            }
            console.log(`Updated mapping for degree ${degreeId}`);
          }
        });

        await Promise.all(updatePromises);
      }

      alert('Module mappings updated successfully!');
      
      // Reload data to reflect changes
      await loadData();

    } catch (error) {
      console.error('Error updating mappings:', error);
      alert('Error updating mappings: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{width: '100%', height: '100%', position: 'relative', background: 'white'}}>
      <Sidebar />
      
      <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh'}}>
        <Header />
        
        {!currentUser ? (
          <div style={{margin: '40px', textAlign: 'center', padding: '40px'}}>
            <h2 style={{color: '#F04915', fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>
              Please Log In
            </h2>
            <p style={{fontSize: 16, fontFamily: 'Outfit', color: '#77838F'}}>
              You need to be logged in as an admin to edit module mappings.
            </p>
            <Link to="/login">
              <button style={{
                padding: '12px 24px',
                background: '#F04915',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: 16,
                fontFamily: 'Outfit',
                fontWeight: '500',
                cursor: 'pointer',
                marginTop: '20px'
              }}>
                Go to Login
              </button>
            </Link>
          </div>
        ) : (
        <form onSubmit={handleSubmit}>
          <div style={{margin: '20px 40px', position: 'relative'}}>
            <div style={{background: '#F04915', borderTopLeftRadius: 15, borderTopRightRadius: 15, padding: '20px', color: 'white'}}>
              <h2 style={{fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>Edit Module to Degree Programme Mapping</h2>
            </div>

            <div style={{background: '#FFFBF5', padding: '40px'}}>
              {loading && (
                <div style={{textAlign: 'center', marginBottom: '20px'}}>
                  <div style={{color: '#F04915', fontSize: 16, fontFamily: 'Outfit'}}>
                    Loading data...
                  </div>
                </div>
              )}
              
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                {/* Left Column */}
                <div>
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Select Module <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="selectedModuleId"
                      value={formData.selectedModuleId}
                      onChange={handleModuleChange}
                      required
                      style={{
                        width: '100%', 
                        height: 49, 
                        padding: '0 15px', 
                        borderRadius: 8, 
                        border: formData.selectedModuleId ? '2px solid #28a745' : '1px solid #77838F', 
                        fontSize: 16, 
                        fontFamily: 'Outfit',
                        backgroundColor: formData.selectedModuleId ? '#f8fff8' : 'white'
                      }}
                    >
                      <option value="">Select Module</option>
                      {modulesList.map(module => (
                        <option key={module.id} value={module.id}>
                          {module.module_name} ({module.module_code})
                        </option>
                      ))}
                    </select>
                    {formData.selectedModuleId && (
                      <div style={{
                        fontSize: 14,
                        color: '#28a745',
                        marginTop: '4px',
                        fontFamily: 'Outfit'
                      }}>
                        ✓ Selected: {formData.selectedModule}
                        {existingMappings.filter(m => m.module_id === formData.selectedModuleId).length > 0 && (
                          <div style={{fontSize: 12, color: '#17a2b8', marginTop: '2px'}}>
                            📋 Loaded {existingMappings.filter(m => m.module_id === formData.selectedModuleId).length} existing mapping(s)
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Year <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="year"
                      value={formData.year}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%', 
                        height: 49, 
                        padding: '0 15px', 
                        borderRadius: 8, 
                        border: '1px solid #77838F', 
                        fontSize: 16, 
                        fontFamily: 'Outfit',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Select Year</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
                      <input
                        type="checkbox"
                        name="isMandatory"
                        checked={formData.isMandatory}
                        onChange={handleInputChange}
                        style={{
                          marginRight: '12px', 
                          width: '20px', 
                          height: '20px'
                        }}
                      />
                      <span style={{
                        color: 'black', 
                        fontSize: 20, 
                        fontFamily: 'Outfit', 
                        fontWeight: '500'
                      }}>
                        Is Mandatory <span style={{color: '#F04915'}}>*</span>
                      </span>
                    </label>
                  </div>
                </div>

                {/* Right Column */}
                <div>
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Choose Degree <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <div style={{
                      border: '1px solid #77838F',
                      borderRadius: 8,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      background: 'white'
                    }}>
                      {degreeProgramsList.map(program => (
                        <label
                          key={program.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px 15px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseOver={(e) => {
                            e.target.style.backgroundColor = '#f8f9fa';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.backgroundColor = formData.selectedDegreePrograms.includes(program.id) ? '#e7f3ff' : 'white';
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={formData.selectedDegreePrograms.includes(program.id)}
                            onChange={() => {
                              setFormData(prev => ({
                                ...prev,
                                selectedDegreePrograms: prev.selectedDegreePrograms.includes(program.id)
                                  ? prev.selectedDegreePrograms.filter(id => id !== program.id)
                                  : [...prev.selectedDegreePrograms, program.id]
                              }));
                            }}
                            style={{marginRight: '12px', width: '16px', height: '16px'}}
                          />
                          <span style={{fontSize: 16, fontFamily: 'Outfit', color: '#333'}}>
                            {program.displayName}
                          </span>
                        </label>
                      ))}
                    </div>
                    {formData.selectedDegreePrograms.length > 0 && (
                      <div style={{marginTop: '8px', fontSize: 14, fontFamily: 'Outfit', color: '#666'}}>
                        {formData.selectedDegreePrograms.length} programme(s) selected
                      </div>
                    )}
                  </div>

                  {/* Info Box */}
                  <div style={{
                    background: '#FFFBF5',
                    border: '1px solid #F04915',
                    borderRadius: 8,
                    padding: '16px',
                    marginTop: '20px'
                  }}>
                    <h4 style={{
                      fontSize: 16,
                      fontFamily: 'Outfit',
                      fontWeight: '600',
                      color: '#F04915',
                      margin: '0 0 8px 0'
                    }}>
                      ℹ️ Edit Module Mapping
                    </h4>
                    <p style={{
                      fontSize: 14,
                      fontFamily: 'Outfit',
                      color: '#D63A0F',
                      margin: 0,
                      lineHeight: '1.4',
                      textAlign: 'left'
                    }}>
                      1. Select a module to load existing mappings<br/>
                      2. Year and degrees will auto-populate from existing data<br/>
                      3. Modify year, mandatory status, or degree selections<br/>
                      4. Add/remove degree programmes as needed<br/>
                      5. Click "Update Mapping" to save changes
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{margin: '40px', display: 'flex', justifyContent: 'space-between'}}>
            <Link to="/add-module">
              <button 
                type="button" 
                style={{
                  width: 269, 
                  padding: '11px 20px', 
                  background: '#6C757D', 
                  border: 'none', 
                  borderRadius: 8, 
                  color: 'white', 
                  fontSize: 16, 
                  fontFamily: 'Outfit', 
                  fontWeight: '500', 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#5A6268';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#6C757D';
                }}
              >
                Back to Module Management
              </button>
            </Link>
            <button 
              type="submit" 
              disabled={!formData.selectedModuleId || formData.selectedDegreePrograms.length === 0 || !formData.year || loading}
              style={{
                width: 269, 
                padding: '11px 20px', 
                background: (!formData.selectedModuleId || formData.selectedDegreePrograms.length === 0 || !formData.year || loading) ? '#ccc' : '#F04915', 
                border: 'none', 
                borderRadius: 8, 
                color: 'white', 
                fontSize: 16, 
                fontFamily: 'Outfit', 
                fontWeight: '500', 
                cursor: (!formData.selectedModuleId || formData.selectedDegreePrograms.length === 0 || !formData.year || loading) ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                if (!formData.selectedModuleId || formData.selectedDegreePrograms.length === 0 || !formData.year || loading) return;
                e.target.style.background = '#D63A0F';
              }}
              onMouseOut={(e) => {
                if (!formData.selectedModuleId || formData.selectedDegreePrograms.length === 0 || !formData.year || loading) return;
                e.target.style.background = '#F04915';
              }}
            >
              {loading ? 'Updating...' : 'Update Mapping'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default EditMapModule;
